import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Form,
  Input,
  DatePicker,
  TimePicker,
  InputNumber,
  Select,
  Radio,
  Checkbox,
  Button,
  Space,
  Divider,
  message,
  Modal,
  Typography,
  Row,
  Col,
  Card,
  Alert,
} from 'antd';
import { ExclamationCircleOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { WaveRuleDTO, CreateWaveRuleRequest, UpdateWaveRuleRequest } from '../../../../types/wave/waveRule';
import type { Store } from '../../../../types/master-data/store';
import * as waveRuleService from '../../../../services/wave/waveRuleService';
import {
  WaveTimeReferenceTypeOptions,
  TaskTriggerTypeOptions,
  TaskTriggerType,
  LaunchCycle,
  LaunchCycleOptions,
  WeekdayOptions,
  EntryConditionType,
  EntryConditionTypeOptions,
} from '../../../../constants/wave';
import BatchAddStoreModal from './BatchAddStoreModal';

const { Title, Text } = Typography;

// Wave execution mode enum
enum WaveExecutionMode {
  MANUAL_PICKING = 'MANUAL_PICKING',
  ORDER_PICKING = 'ORDER_PICKING',
  BATCH_PICKING = 'BATCH_PICKING',
}

// Store list for dropdown selection (all stores)
const ALL_STORE_OPTIONS: { label: string; value: string }[] = [
  // Go Fresh stores
  { label: 'Fresh Downtown', value: 'GF001' },
  { label: 'Fresh Midtown', value: 'GF002' },
  { label: 'Fresh Uptown', value: 'GF003' },
  { label: 'Fresh Riverside', value: 'GF004' },
  { label: 'Fresh Central', value: 'GF005' },
  // Supermarket stores
  { label: 'Supermart Plaza', value: 'SM001' },
  { label: 'Supermart Mall', value: 'SM002' },
  { label: 'Supermart Square', value: 'SM003' },
  { label: 'Supermart Center', value: 'SM004' },
  { label: 'Supermart Hub', value: 'SM005' },
  // Hyper stores
  { label: 'Hyper Mega Store', value: 'HP001' },
  { label: 'Hyper Giant Mart', value: 'HP002' },
  { label: 'Hyper Super Center', value: 'HP003' },
  { label: 'Hyper World', value: 'HP004' },
  { label: 'Hyper Land', value: 'HP005' },
];

interface WaveRuleFormProps {
  visible: boolean;
  ruleCode: string | null; // null for create, string for edit
  onSuccess: () => void;
  onCancel: () => void;
}

const WaveRuleForm: React.FC<WaveRuleFormProps> = ({ visible, ruleCode, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [executionMode, setExecutionMode] = useState<WaveExecutionMode>(WaveExecutionMode.BATCH_PICKING);
  const [launchCycle, setLaunchCycle] = useState<LaunchCycle>(LaunchCycle.DAILY);
  
  // Batch Add Store states
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [batchAddModalVisible, setBatchAddModalVisible] = useState(false);

  // Channel options - loaded from backend
  const [channelOptions, setChannelOptions] = useState<{ value: string; label: string }[]>([]);

  // Launch time periods state - array of [start, end] time ranges
  const [launchTimePeriods, setLaunchTimePeriods] = useState<[Dayjs | null, Dayjs | null][]>([]);

  // Watch form values change
  const watchTaskTriggerTypes = Form.useWatch('taskTriggerTypes', form);
  const watchLaunchCycle = Form.useWatch('launchCycle', form);
  const watchEntryConditions = Form.useWatch('entryConditions', form);

  const isEditMode = !!ruleCode;

  // Update launch cycle when form value changes
  useEffect(() => {
    if (watchLaunchCycle) {
      setLaunchCycle(watchLaunchCycle);
    }
  }, [watchLaunchCycle]);

  // Load channel options from backend on mount
  useEffect(() => {
    waveRuleService.getChannelOptions().then((res) => {
      if (res.success && res.data) {
        setChannelOptions(res.data.map((o) => ({ value: o.value, label: o.label })));
      }
    });
  }, []);

  // Load data for edit mode
  useEffect(() => {
    if (visible && ruleCode) {
      loadRuleData(ruleCode);
    } else if (visible) {
      // Set default values for create mode
      const defaultValues = {
        taskTriggerTypes: [TaskTriggerType.BY_ORDER_COUNT],
        executionMode: WaveExecutionMode.BATCH_PICKING,
        launchCycle: LaunchCycle.DAILY,
        effectiveStartDate: null,
        effectiveEndDate: null,
      };
      form.setFieldsValue(defaultValues);
      setInitialValues(defaultValues);
      setLaunchCycle(LaunchCycle.DAILY);
      // Initialize with one empty time period
      setLaunchTimePeriods([[null, null]]);
    }
  }, [visible, ruleCode]);

  // Load rule data
  const loadRuleData = async (code: string) => {
    setLoading(true);
    try {
      const response = await waveRuleService.getWaveRuleByCode(code);
      if (response.success && response.data) {
        const rule = response.data;
        // Determine execution mode from rule data (default to BATCH_PICKING)
        const ruleExecutionMode = (rule as any).executionMode || WaveExecutionMode.BATCH_PICKING;
        // Parse launch time periods from launchTimePeriods
        const periods: [Dayjs | null, Dayjs | null][] = [];
        if (rule.launchTimePeriods && rule.launchTimePeriods.length > 0) {
          for (const period of rule.launchTimePeriods) {
            periods.push([
              dayjs(period.startTime, 'HH:mm'),
              dayjs(period.endTime, 'HH:mm'),
            ]);
          }
        }
        // Ensure at least one period exists
        if (periods.length === 0) {
          periods.push([null, null]);
        }
        setLaunchTimePeriods(periods);

        // 根据 applyType 回填两种互斥模式的表单字段：
        // - STORE_TYPE：回填 applyStoreTypes（由 applyStoreFormats 推导）
        // - STORE_LIST：根据 applyStores 拉取具体门店详情回填 selectedStores 表格
        const applyType = (rule as any).applyType;
        const applyStoreFormats: string[] = (rule as any).applyStoreFormats || [];
        const applyStores: string[] = (rule as any).applyStores || [];
        const isStoreTypeMode = applyType === 'STORE_TYPE';

        const formValues = {
          ruleName: rule.ruleName,
          description: rule.description,
          effectiveStartDate: rule.effectiveStartDate ? dayjs(rule.effectiveStartDate) : null,
          effectiveEndDate: rule.effectiveEndDate ? dayjs(rule.effectiveEndDate) : null,
          timeReferenceType: rule.timeReferenceType,
          // 回填波次进入条件：Order Create Time 时间范围 + Channel 白名单
          entryConditionTimeRange:
            (rule as any).entryConditionStartTime && (rule as any).entryConditionEndTime
              ? [
                  dayjs((rule as any).entryConditionStartTime, 'HH:mm'),
                  dayjs((rule as any).entryConditionEndTime, 'HH:mm'),
                ]
              : undefined,
          entryConditionChannels: (rule as any).entryConditionChannels || [],
          taskTriggerTypes: rule.taskTriggerTypes || [],
          triggerOrderCount: rule.triggerOrderCount,
          triggerTimeMinutes: rule.triggerTimeMinutes,
          launchCycle: rule.launchCycle || LaunchCycle.DAILY,
          dailyInterval: rule.dailyInterval,
          weekdays: rule.weekdays,
          applyStoreTypes: isStoreTypeMode ? applyStoreFormats : [],
          excludeStores: (rule as any).excludeStores || [],
          executionMode: ruleExecutionMode,
        };
        form.setFieldsValue(formValues);
        setInitialValues(formValues);
        setExecutionMode(ruleExecutionMode as WaveExecutionMode);
        setLaunchCycle(rule.launchCycle || LaunchCycle.DAILY);

        // STORE_LIST 模式：根据 applyStores 回填 selectedStores，交由 BatchAddStoreModal 自行匹配门店明细
        if (!isStoreTypeMode && applyStores.length > 0) {
          const fallbackStores: Store[] = applyStores.map(code => ({ storeCode: code, storeName: code } as Store));
          setSelectedStores(fallbackStores);
        } else {
          setSelectedStores([]);
        }
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle form value change
  const handleFormChange = () => {
    setHasChanges(true);
  };

  // Check if batch picking mode is selected
  const isBatchPicking = executionMode === WaveExecutionMode.BATCH_PICKING;

  // Handle submit
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Only validate and convert launch time periods for BATCH_PICKING mode
      let apiLaunchTimePeriods: { startTime: string; endTime: string }[] = [];

      if (executionMode === WaveExecutionMode.BATCH_PICKING) {
        const validPeriods = launchTimePeriods.filter(
          (period): period is [Dayjs, Dayjs] => period[0] !== null && period[1] !== null
        );

        if (validPeriods.length === 0) {
          message.error(t('wave.waveRule.form.rules.launchTimePeriodsRequired'));
          setLoading(false);
          return;
        }

        // Sort periods by start time
        validPeriods.sort((a, b) => a[0].valueOf() - b[0].valueOf());

        // Convert to API format: array of {startTime, endTime} objects
        apiLaunchTimePeriods = validPeriods.map(period => ({
          startTime: period[0].format('HH:mm'),
          endTime: period[1].format('HH:mm'),
        }));
      }

      // 收集用户选中的适用门店编码列表（来自 BatchAddStoreModal 的 selectedStores）
      const applyStoreCodeList = selectedStores.map((store) => store.storeCode);
      // 根据 BatchAddStoreModal 的互斥模式推导 applyType：
      // - applyStoreTypes 有值 → STORE_TYPE模式，后端基于业态解析门店
      // - 否则 → STORE_LIST模式，使用用户选定的门店编码列表
      const applyStoreTypes: string[] = form.getFieldValue('applyStoreTypes') || [];
      const isStoreTypeMode = applyStoreTypes.length > 0;
      const applyType: 'STORE_TYPE' | 'STORE_LIST' = isStoreTypeMode ? 'STORE_TYPE' : 'STORE_LIST';

      // 必填校验：按业态模式需 applyStoreFormats 非空；按门店模式需 applyStores 非空
      if (!isEditMode) {
        if (isStoreTypeMode && applyStoreTypes.length === 0) {
          message.error(t('wave.waveRule.form.rules.applyStoresRequired'));
          setLoading(false);
          return;
        }
        if (!isStoreTypeMode && applyStoreCodeList.length === 0) {
          message.error(t('wave.waveRule.form.rules.applyStoresRequired'));
          setLoading(false);
          return;
        }
      }

      // 解析波次进入条件：TimePicker.RangePicker 拆分为 entryConditionStartTime/EndTime
      const entryTimeRange: [Dayjs | null, Dayjs | null] | undefined = values.entryConditionTimeRange;
      const entryConditionStartTime = entryTimeRange && entryTimeRange[0]
        ? entryTimeRange[0].format('HH:mm')
        : undefined;
      const entryConditionEndTime = entryTimeRange && entryTimeRange[1]
        ? entryTimeRange[1].format('HH:mm')
        : undefined;
      const entryConditionChannels: string[] = values.entryConditionChannels || [];

      // Convert form values to API request format
      const requestData: any = {
        ruleName: values.ruleName,
        description: values.description,
        effectiveStartDate: values.effectiveStartDate ? values.effectiveStartDate.valueOf() : undefined,
        effectiveEndDate: values.effectiveEndDate ? values.effectiveEndDate.valueOf() : undefined,
        timeReferenceType: values.timeReferenceType,
        entryConditionStartTime,
        entryConditionEndTime,
        entryConditionChannels,
        // Use array directly from Checkbox.Group
        taskTriggerTypes: values.taskTriggerTypes || [],
        triggerOrderCount: values.triggerOrderCount,
        triggerTimeMinutes: values.triggerTimeMinutes,
        launchCycle: values.launchCycle,
        dailyInterval: values.dailyInterval,
        weekdays: values.weekdays,
        launchTimePeriods: apiLaunchTimePeriods,
        // 新增：应用类型 + 两种模式下的目标字段（必传给后端）
        applyType: applyType,
        applyStoreFormats: isStoreTypeMode ? applyStoreTypes : [],
        applyStores: isStoreTypeMode ? [] : applyStoreCodeList,
        applyStoreTypes: applyStoreTypes,
        excludeStores: form.getFieldValue('excludeStores') || [],
        executionMode: values.executionMode,
      };

      let response;
      if (isEditMode) {
        response = await waveRuleService.updateWaveRule({ ruleCode: ruleCode!, ...requestData });
      } else {
        response = await waveRuleService.createWaveRule(requestData as CreateWaveRuleRequest);
      }

      if (response.success) {
        message.success(
          isEditMode
            ? t('wave.waveRule.form.message.updateSuccess')
            : t('wave.waveRule.form.message.createSuccess')
        );
        form.resetFields();
        setHasChanges(false);
        onSuccess();
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      message.error(t('common.message.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasChanges) {
      Modal.confirm({
        title: t('account.unsavedChangesTitle'),
        icon: <ExclamationCircleOutlined />,
        content: t('account.unsavedChangesContent'),
        okText: t('common.confirm'),
        cancelText: t('common.cancel'),
        onOk: () => {
          form.resetFields();
          setHasChanges(false);
          setLaunchTimePeriods([[null, null]]);
          onCancel();
        },
      });
    } else {
      form.resetFields();
      setLaunchTimePeriods([[null, null]]);
      onCancel();
    }
  };

  // Add a new launch time period
  const handleAddTimePeriod = () => {
    setLaunchTimePeriods([...launchTimePeriods, [null, null]]);
    setHasChanges(true);
  };

  // Remove a launch time period
  const handleRemoveTimePeriod = (index: number) => {
    if (launchTimePeriods.length > 1) {
      setLaunchTimePeriods(launchTimePeriods.filter((_, i) => i !== index));
      setHasChanges(true);
    }
  };

  // Update a launch time period
  const handleTimePeriodChange = (index: number, times: [Dayjs | null, Dayjs | null] | null) => {
    const newPeriods = [...launchTimePeriods];
    newPeriods[index] = times || [null, null];
    setLaunchTimePeriods(newPeriods);
    setHasChanges(true);
  };

  // Handle execution mode change
  const handleExecutionModeChange = (e: any) => {
    const newMode = e.target.value;
    setExecutionMode(newMode);
    setHasChanges(true);
  };

  // Handle batch add stores confirm
  const handleBatchAddConfirm = (stores: Store[], applyStoreTypes: string[], excludeStores: string[]) => {
    // Update form values with applyStoreTypes and excludeStores
    form.setFieldsValue({
      applyStoreTypes,
      excludeStores,
    });

    // 直接替换为新的门店列表
    setSelectedStores(stores);
    setBatchAddModalVisible(false);
    setHasChanges(true);
    message.success(t('wave.waveRule.form.batchAddStore.message.addSuccess', { count: stores.length }));
  };

  return (
    <Drawer
      title={isEditMode ? t('wave.waveRule.form.title.edit') : t('wave.waveRule.form.title.create')}
      width="50vw"
      open={visible}
      onClose={handleCancel}
      destroyOnClose
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>{t('common.cancel')}</Button>
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              {t('common.save')}
            </Button>
          </Space>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
      >
        {/* Wave Execution Mode Selection */}
        <Title level={4} style={{ marginBottom: 16 }}>{t('wave.waveRule.form.section.executionMode')}</Title>
        <Alert
          message={t('wave.waveRule.form.fields.executionModeDescription')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form.Item
          name="executionMode"
          rules={[{ required: true, message: t('wave.waveRule.form.rules.executionModeRequired') }]}
        >
          <Radio.Group onChange={handleExecutionModeChange}>
            <Radio value={WaveExecutionMode.MANUAL_PICKING}>{t('wave.waveRule.form.fields.manualPicking')}</Radio>
            <Radio value={WaveExecutionMode.ORDER_PICKING}>{t('wave.waveRule.form.fields.orderPicking')}</Radio>
            <Radio value={WaveExecutionMode.BATCH_PICKING}>{t('wave.waveRule.form.fields.batchPicking')}</Radio>
          </Radio.Group>
        </Form.Item>
      
        {/* Apply Store Types Section - Show for all modes */}
        <Title level={4} style={{ marginBottom: 16 }}>
          {t('wave.waveRule.form.section.applyStoreTypes')}
        </Title>
        
        {/* Batch Add Button */}
        <Form.Item>
          <Button 
            icon={<PlusOutlined />} 
            onClick={() => setBatchAddModalVisible(true)}
          >
            {t('wave.waveRule.form.selectStore.batchAdd')}
          </Button>
        </Form.Item>
      
        {/* Basic Information */}
        <Divider orientation="left">{t('wave.waveRule.form.section.basicInfo')}</Divider>

        {/* Rule Name - 单独一排 */}
        <Form.Item
          name="ruleName"
          label={t('wave.waveRule.form.fields.ruleName')}
          rules={[
            { required: true, message: t('wave.waveRule.form.rules.ruleNameRequired') },
            { max: 100, message: t('common.validation.maxLength', { max: 100 }) },
          ]}
        >
          <Input placeholder={t('wave.waveRule.form.placeholder.ruleName')} />
        </Form.Item>

        {/* Effective Date - show for all modes */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="effectiveStartDate"
              label={t('wave.waveRule.form.fields.effectiveStartDate')}
              rules={[{ required: true, message: t('wave.waveRule.form.rules.effectiveStartDateRequired') }]}
            >
              <DatePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="effectiveEndDate"
              label={t('wave.waveRule.form.fields.effectiveEndDate')}
            >
              <DatePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        {/* Description - 单独一排 */}
        <Form.Item
          name="description"
          label={t('wave.waveRule.form.fields.description')}
          rules={[{ max: 500, message: t('common.validation.maxLength', { max: 500 }) }]}
        >
          <Input.TextArea
            rows={2}
            placeholder={t('wave.waveRule.form.placeholder.description')}
          />
        </Form.Item>

        {/* Wave Entry Condition - Only for Batch Picking */}
        {isBatchPicking && (
          <>
            <Divider orientation="left">{t('wave.waveRule.form.section.waveCondition')}</Divider>

            {/* Configuration Description */}
            <div style={{
              background: '#e6f4ff',
              borderRadius: 8,
              padding: '12px 16px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8
            }}>
              <ExclamationCircleOutlined style={{ color: '#1677ff', marginTop: 2 }} />
              <span style={{ color: '#0958d9', fontSize: 14 }}>
                {t('wave.waveRule.form.fields.entryConditionDescription')}
              </span>
            </div>

            {/* Order Create Time */}
            <Form.Item
              name="entryConditionTimeRange"
              label={t('wave.waveRule.form.fields.orderCreateTime')}
            >
              <TimePicker.RangePicker
                format="HH:mm"
                style={{ width: '100%' }}
                placeholder={[
                  t('wave.waveRule.form.placeholder.startTime'),
                  t('wave.waveRule.form.placeholder.endTime')
                ]}
              />
            </Form.Item>

            {/* Channel */}
            <Form.Item
              name="entryConditionChannels"
              label={t('wave.waveRule.form.fields.channel')}
            >
              <Select
                mode="multiple"
                placeholder={t('wave.waveRule.form.placeholder.selectChannels')}
                options={channelOptions}
              />
            </Form.Item>
          </>
        )}

        {/* Task Generation Strategy - Only for Batch Picking */}
        {isBatchPicking && (
          <>
            <Divider orientation="left">{t('wave.waveRule.form.section.waveStrategy')}</Divider>

            {/* Configuration Description */}
            <div style={{
              background: '#e6f4ff',
              borderRadius: 8,
              padding: '12px 16px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8
            }}>
              <ExclamationCircleOutlined style={{ color: '#1677ff', marginTop: 2 }} />
              <span style={{ color: '#0958d9', fontSize: 14 }}>
                {t('wave.waveRule.form.fields.waveStrategyDescription')}
              </span>
            </div>

            {/* Multi-select Checkbox for trigger types */}
            <Form.Item
              name="taskTriggerTypes"
              label={t('wave.waveRule.form.fields.taskTriggerTypes')}
              rules={[{ required: isBatchPicking, message: t('wave.waveRule.form.rules.taskTriggerTypesRequired') }]}
            >
              <Checkbox.Group
                options={TaskTriggerTypeOptions.map(opt => ({
                  label: t(opt.label),
                  value: opt.value,
                }))}
              />
            </Form.Item>

            {watchTaskTriggerTypes?.includes(TaskTriggerType.BY_ORDER_COUNT) && (
              <Form.Item
                name="triggerOrderCount"
                label={t('wave.waveRule.form.fields.triggerOrderCount')}
                rules={[{ required: true, message: t('wave.waveRule.form.rules.triggerOrderCountRequired') }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            )}

            {watchTaskTriggerTypes?.includes(TaskTriggerType.BY_TIME_INTERVAL) && (
              <Form.Item
                name="triggerTimeMinutes"
                label={t('wave.waveRule.form.fields.triggerTimeMinutes')}
                rules={[{ required: true, message: t('wave.waveRule.form.rules.triggerTimeMinutesRequired') }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            )}
          </>
        )}

        {/* Wave Launch Cycle Configuration - Only for Batch Picking */}
        {isBatchPicking && (
          <>
            <Divider orientation="left">{t('wave.waveRule.form.section.launchCycle')}</Divider>
            
            {/* Configuration Description */}
            <div style={{ 
              background: '#e6f4ff', 
              borderRadius: 8, 
              padding: '12px 16px', 
              marginBottom: 16,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8
            }}>
              <ExclamationCircleOutlined style={{ color: '#1677ff', marginTop: 2 }} />
              <span style={{ color: '#0958d9', fontSize: 14 }}>
                {t('wave.waveRule.form.fields.launchCycleDescription')}
              </span>
            </div>

            <Form.Item
              name="launchCycle"
              label={t('wave.waveRule.form.fields.launchCycle')}
              rules={[{ required: true, message: t('wave.waveRule.form.rules.launchCycleRequired') }]}
            >
              <Radio.Group>
                {LaunchCycleOptions.map(opt => (
                  <Radio key={opt.value} value={opt.value}>
                    {t(opt.label)}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>

            {/* Daily Interval - Show when DAILY is selected */}
            {launchCycle === LaunchCycle.DAILY && (
              <Form.Item
                name="dailyInterval"
                label={t('wave.waveRule.form.fields.dailyInterval')}
                rules={[{ required: true, message: t('wave.waveRule.form.rules.dailyIntervalRequired') }]}
              >
                <InputNumber min={1} max={365} style={{ width: '100%' }} placeholder={t('wave.waveRule.form.placeholder.dailyInterval')} />
              </Form.Item>
            )}

            {/* Weekdays - Show when WEEKLY is selected */}
            {launchCycle === LaunchCycle.WEEKLY && (
              <Form.Item
                name="weekdays"
                label={t('wave.waveRule.form.fields.weekdays')}
                rules={[{ required: true, message: t('wave.waveRule.form.rules.weekdaysRequired') }]}
              >
                <Checkbox.Group>
                  {WeekdayOptions.map(opt => (
                    <Checkbox key={opt.value} value={opt.value}>
                      {t(opt.label)}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>
            )}
          </>
        )}

        {/* Wave Launch Time Configuration - Only for Batch Picking */}
        {isBatchPicking && (
          <>
            <Divider orientation="left">{t('wave.waveRule.form.section.launchTime')}</Divider>

            {/* Launch Time Periods Description */}
            <div style={{
              background: '#e6f4ff',
              borderRadius: 8,
              padding: '12px 16px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8
            }}>
              <ExclamationCircleOutlined style={{ color: '#1677ff', marginTop: 2 }} />
              <span style={{ color: '#0958d9', fontSize: 14 }}>
                {t('wave.waveRule.form.fields.launchTimePeriodsDescription')}
              </span>
            </div>

            {/* Launch Time Periods */}
            <Form.Item
              label={t('wave.waveRule.form.fields.launchTimePeriods')}
              required
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {launchTimePeriods.map((period, index) => (
                  <Card
                    key={index}
                    size="small"
                    style={{ marginBottom: 8 }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <Row gutter={16} align="middle">
                      <Col flex="auto">
                        <TimePicker.RangePicker
                          format="HH:mm"
                          value={period}
                          onChange={(times) => handleTimePeriodChange(index, times as [Dayjs | null, Dayjs | null])}
                          style={{ width: '100%' }}
                          placeholder={[
                            t('wave.waveRule.form.placeholder.startTime'),
                            t('wave.waveRule.form.placeholder.endTime')
                          ]}
                        />
                      </Col>
                      <Col flex="none">
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveTimePeriod(index)}
                          disabled={launchTimePeriods.length <= 1}
                        />
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={handleAddTimePeriod}
                  style={{ width: '100%' }}
                >
                  {t('wave.waveRule.form.actions.addTimePeriod')}
                </Button>
              </Space>
            </Form.Item>
          </>
        )}
      </Form>
      
      {/* Batch Add Store Modal */}
      <BatchAddStoreModal
        visible={batchAddModalVisible}
        onCancel={() => setBatchAddModalVisible(false)}
        onConfirm={handleBatchAddConfirm}
        existingStoreCodes={[]}
        initialSelectedStoreCodes={selectedStores.map(s => s.storeCode)}
        initialApplyStoreTypes={form.getFieldValue('applyStoreTypes') || []}
        initialExcludeStores={form.getFieldValue('excludeStores') || []}
      />
    </Drawer>
  );
};

export default WaveRuleForm;
